import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

const StarRow = ({ rating = 0, size = 14 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${rating} yıldız`}>
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        size={size}
        fill={s <= Math.round(rating) ? '#F5B301' : 'transparent'}
        stroke="#F5B301"
      />
    ))}
  </div>
);

const ReviewCard = ({ r }) => {
  const [expanded, setExpanded] = useState(false);
  const long = (r.text || '').length > 220;
  const displayText = expanded || !long ? r.text : `${r.text.slice(0, 220)}…`;

  return (
    <article
      className="flex flex-col bg-white rounded-2xl border border-gray-100 hover:border-[#3D4A26]/30 hover:shadow-lg transition-all duration-300 p-6 h-full"
      data-testid="google-review-card"
    >
      <div className="flex items-center gap-3 mb-3">
        {r.avatar ? (
          <img
            src={r.avatar}
            alt={r.author}
            referrerPolicy="no-referrer"
            className="w-10 h-10 rounded-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#3D4A26] text-white flex items-center justify-center text-sm font-bold">
            {initials(r.author) || 'G'}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#1B1B1B] truncate">{r.author || 'Google Kullanıcı'}</p>
          <p className="text-xs text-gray-500">{r.relativeTime}</p>
        </div>
      </div>
      <StarRow rating={r.rating} />
      <p className="mt-3 text-sm text-gray-700 leading-relaxed flex-1">
        &ldquo;{displayText}&rdquo;
      </p>
      {long && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 self-start text-xs font-semibold text-[#3D4A26] hover:underline"
          data-testid="review-toggle-btn"
        >
          {expanded ? 'Daha az göster' : 'Devamını oku'}
        </button>
      )}
    </article>
  );
};

const GoogleReviews = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/google-reviews`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data || !data.reviews || data.reviews.length === 0) return null;

  const reviews = data.reviews.slice(0, 3);

  return (
    <section className="py-16 md:py-24 bg-[#f7f4ec]" data-testid="google-reviews">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-3">
              {/* Inline Google G logo — no external script */}
              <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#3D4A26]">
                Google Değerlendirmeleri
              </p>
            </div>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#1B1B1B] leading-tight mb-3"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Katılımcılarımız ne diyor?
            </h2>
            <div className="flex items-center gap-3">
              <StarRow rating={data.rating} size={16} />
              <span className="text-sm text-[#1B1B1B]">
                <strong>{Number(data.rating).toFixed(1)}</strong> / 5 &nbsp;·&nbsp;{' '}
                <span className="text-gray-600">{data.totalReviews}+ Google değerlendirmesi</span>
              </span>
            </div>
          </div>
          {data.googleMapsUri && (
            <a
              href={data.googleMapsUri}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-cta"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1B1B1B] hover:text-[#3D4A26] transition-colors w-fit"
            >
              Google&apos;da tümünü gör
              <ExternalLink size={14} />
            </a>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {reviews.map((r, i) => (
            <ReviewCard key={i} r={r} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
